import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

/**
 * Session timeout hook
 * Monitors JWT token expiration and redirects to login when expired
 * Keeps session alive while user is online by refreshing the JWT before expiry.
 */
export const useSessionTimeout = () => {
  const navigate = useNavigate();
  const { logout: authLogout, updateToken } = useAuth();
  const [showWarning] = useState(false);
  const [timeLeft] = useState(null);
  const refreshingRef = useRef(false);
  const lastRefreshAtRef = useRef(0);
  const lastActiveAtRef = useRef(Date.now());

  const decodeToken = useCallback((token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Failed to decode token:', error);
      return null;
    }
  }, []);

  const logout = useCallback(() => {
    authLogout?.();
    navigate('/login', { state: { message: 'Your session has expired. Please log in again.' } });
  }, [authLogout, navigate]);

  const resetWarning = useCallback(() => {
    // no-op (warning modal disabled)
  }, []);

  useEffect(() => {
    const bumpActivity = () => { lastActiveAtRef.current = Date.now(); };
    window.addEventListener('mousemove', bumpActivity, { passive: true });
    window.addEventListener('keydown', bumpActivity, { passive: true });
    window.addEventListener('pointerdown', bumpActivity, { passive: true });
    window.addEventListener('touchstart', bumpActivity, { passive: true });
    window.addEventListener('focus', bumpActivity, { passive: true });
    document.addEventListener('visibilitychange', bumpActivity, { passive: true });

    return () => {
      window.removeEventListener('mousemove', bumpActivity);
      window.removeEventListener('keydown', bumpActivity);
      window.removeEventListener('pointerdown', bumpActivity);
      window.removeEventListener('touchstart', bumpActivity);
      window.removeEventListener('focus', bumpActivity);
      document.removeEventListener('visibilitychange', bumpActivity);
    };
  }, []);

  const tryRefresh = useCallback(async () => {
    if (refreshingRef.current) return false;
    const now = Date.now();
    if (now - lastRefreshAtRef.current < 60 * 1000) return false; // debounce
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;

    refreshingRef.current = true;
    lastRefreshAtRef.current = now;
    try {
      const res = await authAPI.refresh();
      const newToken = res?.data?.token;
      if (newToken) {
        updateToken?.(newToken);
        return true;
      }
      return false;
    } catch {
      return false;
    } finally {
      refreshingRef.current = false;
    }
  }, [updateToken]);

  useEffect(() => {
    // Check token expiration periodically and refresh when close to expiring.
    const interval = setInterval(async () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      const decoded = decodeToken(token);
      if (!decoded || !decoded.exp) return;

      const nowSec = Math.floor(Date.now() / 1000);
      const expiresIn = decoded.exp - nowSec;

      if (expiresIn <= 0) {
        clearInterval(interval);
        logout();
        return;
      }

      // Keep-alive: if we're within 15 minutes of expiry and the user is "online"
      // (tab open + recently active), refresh the session silently.
      const refreshWindowSec = 15 * 60;
      const activeWindowMs = 15 * 60 * 1000;
      const recentlyActive = (Date.now() - lastActiveAtRef.current) <= activeWindowMs;
      const visible = typeof document === 'undefined' ? true : document.visibilityState !== 'hidden';

      if (expiresIn <= refreshWindowSec && (recentlyActive || visible)) {
        await tryRefresh();
      }
    }, 30000); // every 30s

    return () => clearInterval(interval);
  }, [decodeToken, logout, tryRefresh]);

  const dismissWarning = useCallback(() => {
    // no-op (warning modal disabled)
  }, []);

  return {
    showWarning,
    timeLeft,
    logout,
    dismissWarning,
  };
};
